// Global 401 → /refresh → retry wrapper around window.fetch. The Auth0
// access_token cookie expires every ~1 hour by default; the 30-day
// refresh_token cookie can mint a fresh one. Without this wrapper, the
// next API call after the access_token expires fails with 401 and the
// app bounces to login. With it, the wrapper transparently calls
// /refresh once and retries the original request — so users only see
// the login screen when the refresh_token itself is exhausted (30 days).
//
// Companion to useSessionRefreshOnActivity, which refreshes on focus,
// visibility and user activity when the token is near its end. The
// interceptor is the safety net for whatever that misses: a request fired
// after a long sleep, or a token that lapsed with nobody at the keyboard.

// refreshSessionOnce is the SHARED single-flight guard (in loginUtils) that
// the AuthContext proactive timer also funnels through. Both drivers must use
// the same in-flight promise: with Auth0 refresh-token rotation, a 401-retry
// and a timer tick firing at once would otherwise send the same refresh_token
// twice and trip breach detection → whole-family revocation → forced logout.
// It uses tryRefreshSession (not trySilentRefresh) so the native app's
// Keychain fallback runs; its internal calls use rawFetch, so they don't
// recurse back through this interceptor.
import { refreshSessionOnce } from "./loginUtils";

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function isBackendRequest(input: RequestInfo | URL): boolean {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (!backendUrl) return false;
  return urlOf(input).startsWith(backendUrl);
}

function pathOf(input: RequestInfo | URL): string {
  try {
    return new URL(urlOf(input), window.location.origin).pathname;
  } catch {
    return "";
  }
}

// Endpoints that are part of the auth dance itself — refreshing in
// response to their 401 would loop or step on their own cookie rotation.
const SKIP_PATHS = new Set([
  "/refresh",
  "/tokens",
  "/logout",
  "/csrf-token",
]);

let installed = false;

export function installFetchInterceptor(): void {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    // If the caller passed a Request, clone it so we can replay the
    // body on retry — Request bodies can only be consumed once.
    const isRequestInput = input instanceof Request;
    const inputForRetry: RequestInfo | URL = isRequestInput
      ? (input as Request).clone()
      : input;

    const send = (i: RequestInfo | URL, n?: RequestInit) =>
      isRequestInput
        ? originalFetch((i as Request).clone(), n)
        : originalFetch(i, n);

    const response = await send(input, init);

    if (response.status !== 401) return response;
    if (!isBackendRequest(input)) return response;
    if (SKIP_PATHS.has(pathOf(input))) return response;

    const refreshed = await refreshSessionOnce();
    if (!refreshed.ok) return response;

    return send(inputForRetry, init);
  };
}
